import type { ScheduleType } from '@repo/api-codegen';
import type { IconNames } from '@repo/foundations';

export const RUN_STATUS = {
	COMPLETED: 'completed',
	SUCCESS: 'success',
	FAILED: 'failed',
	ERROR: 'error',
	RUNNING: 'running',
	IN_PROGRESS: 'in_progress',
	PENDING: 'pending',
} as const;

export const RUN_STATUS_ICONS: Record<string, IconNames> = {
	[RUN_STATUS.COMPLETED]: 'check',
	[RUN_STATUS.SUCCESS]: 'check',
	[RUN_STATUS.FAILED]: 'alertCircle',
	[RUN_STATUS.ERROR]: 'alertCircle',
	[RUN_STATUS.RUNNING]: 'playerPlay',
	[RUN_STATUS.IN_PROGRESS]: 'playerPlay',
	[RUN_STATUS.PENDING]: 'clock',
};

export const RUN_STATUS_LABELS: Record<string, string> = {
	[RUN_STATUS.COMPLETED]: 'Completed',
	[RUN_STATUS.SUCCESS]: 'Completed',
	[RUN_STATUS.FAILED]: 'Failed',
	[RUN_STATUS.ERROR]: 'Failed',
	[RUN_STATUS.RUNNING]: 'Running',
	[RUN_STATUS.IN_PROGRESS]: 'Running',
	[RUN_STATUS.PENDING]: 'Pending',
};

export const MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
] as const;

export const SCHEDULE_TYPE_OPTIONS: {
	label: string;
	value: ScheduleType;
}[] = [
	// TODO: enable this once we support in the API
	// {
	// 	label: 'Hourly',
	// 	value: 'hourly',
	// },
	// {
	// 	label: 'Daily',
	// 	value: 'daily',
	// },
	// {
	// 	label: 'Weekly',
	// 	value: 'weekly',
	// },
	{
		label: 'Custom (cron)',
		value: 'cron',
	},
];

export const COMMON_CRON_PATTERNS = {
	'0 9 * * 1-5': 'Every weekday morning at 9:00 AM (Monday-Friday)',
	'0 17 * * 1-5': 'Every weekday evening at 5:00 PM (Monday-Friday)',
	'0 0 * * 0': 'Every Sunday at midnight (weekly)',
	'0 0 * * 1': 'Every Monday at midnight (weekly)',
	'0 0 1 * *': 'First day of every month at midnight (monthly)',
	'0 0 1 1 *': 'Every January 1st at midnight (yearly)',
	'*/15 * * * *': 'Every 15 minutes around the clock',
	'0 */2 * * *': 'Every 2 hours around the clock',
	'30 8 * * 1-5': 'Every weekday morning at 8:30 AM (Monday-Friday)',
} as const;

export type RunStatus = keyof typeof RUN_STATUS_ICONS;

/**
 * Standardized notification handling utilities
 */
export const NotificationMessages = {
	AGENT_CREATED: {
		title: 'Success',
		message: 'Agent created successfully',
	},
	AGENT_UPDATED: {
		title: 'Success',
		message: 'Agent updated successfully',
	},
	AGENT_DELETED: {
		title: 'Success',
		message: 'Agent deleted successfully',
	},
	AGENT_EXECUTED: {
		title: 'Success',
		message: 'Agent executed successfully',
	},
	AGENT_SAVE_ERROR: {
		title: 'Error',
		message: 'Failed to save agent',
	},
	AGENT_DELETE_ERROR: {
		title: 'Error',
		message: 'Failed to delete agent',
	},
	AGENT_RUN_ERROR: {
		title: 'Error',
		message: 'Failed to run agent',
	},
} as const;

export const scheduleTypeOptions: {
	label: string;
	value: ScheduleType;
}[] = [
	{
		label: 'Hourly',
		value: 'hourly',
	},
	{
		label: 'Daily',
		value: 'daily',
	},
	{
		label: 'Weekly',
		value: 'weekly',
	},
	{
		label: 'Monthly',
		value: 'monthly',
	},
	{
		label: 'Custom',
		value: 'cron',
	},
];

export const DAYS_OF_WEEK = [
	{
		label: 'Sunday',
		value: '0',
	},
	{
		label: 'Monday',
		value: '1',
	},
	{
		label: 'Tuesday',
		value: '2',
	},
	{
		label: 'Wednesday',
		value: '3',
	},
	{
		label: 'Thursday',
		value: '4',
	},
	{
		label: 'Friday',
		value: '5',
	},
	{
		label: 'Saturday',
		value: '6',
	},
];

export const HOURS_OF_DAY = Array.from({ length: 24 }, (_, i) => {
	const hour = i % 12 || 12; // Convert 0 to 12 for 12 AM
	const period = i < 12 ? 'AM' : 'PM';
	return {
		label: `${hour}:00 ${period} UTC`,
		value: String(i),
	};
});

export const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => {
	const day = i + 1;
	const suffix =
		day === 1 || day === 21 || day === 31
			? 'st'
			: day === 2 || day === 22
				? 'nd'
				: day === 3 || day === 23
					? 'rd'
					: 'th';
	return {
		label: `${day}${suffix}`,
		value: String(day),
	};
});
